/*
 * Copyright (c) 2025, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
// Chakra Components
import {
    Box,
    Flex,
    Stack,
    Text,
    Skeleton,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import ItemVariantProvider from '@salesforce/retail-react-app/app/components/item-variant'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../item-variant/item-name'
import CartItemOptionItems from '../item-variant/item-option-items'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import CartItemVariantPrice from '../item-variant/item-price'
import LoadingSpinner from '../loading-spinner'
import ProductQuantityPicker from './product-quantity-picker'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useStoreContext} from '../../contexts/store'

// Utilities
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

// Hooks
import {useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'
import CartItemBadges from '../item-variant/item-badges'
import QuantityLimit from '../product-view/partials/quantity-limit'
import ContentAsset from '../content-asset'
import {useGlobaleContext} from '../../contexts/globale'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'

const colorBarAssetSkeleton = () => {
    return <Skeleton maxWidth="495px" height="45px" marginBottom="10px" />
}

/**
 * Component representing a product item usually in a list with details about the product - name, variant, pricing, etc.
 * @param {Object} product Product to be represented in the list item.
 * @param {node} primaryAction Child component representing the most prominent action to be performed by the user.
 * @param {node} secondaryActions Child component representing the other actions relevant to the product to be performed by the user.
 * @param {func} onItemQuantityChange callback function to be invoked whenever item quantity changes.
 * @param {boolean} showLoading Renders a loading spinner with overlay if set to true.
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const ProductItem = ({
    product,
    primaryAction,
    globaleUnavailableSKUs = [],
    secondaryActions,
    oosBopisSkus = [],
    outOfStockProductsList = [],
    onItemQuantityChange = noop,
    showLoading = false,
    containerStyles = {},
    isRemoving = false,
    productItem,
    currencySymbol,
    showPriceSkeleton,
    pickupInStore = false,
    isCheckout = false
}) => {
    const {countryISO, currencyCode} = useGlobaleContext()
    const {data: basket} = useCurrentBasket()
    const cartStyles = useMultiStyleConfig('CartStyles')
    const {stepQuantity, quantity, setQuantity} = useDerivedProduct(
        product,
        false,
        false,
        pickupInStore
    )
    let hasOosMetalsAndStones = false
    try {
        hasOosMetalsAndStones =
            product?.c_fluidConfigureMetadata && outOfStockProductsList?.length
                ? Object.values(JSON.parse(product?.c_fluidConfigureMetadata))
                      ?.flat()
                      ?.some((id) => outOfStockProductsList.includes(id))
                : false
    } catch (e) {
        console.warn(e.message)
    }

    const showInventoryMessage =
        outOfStockProductsList?.indexOf(product?.productId) !== -1 || hasOosMetalsAndStones

    const showBopisInventoryMessage =
        pickupInStore &&
        (oosBopisSkus?.length === 0
            ? !!product?.c_fluidConfigureMetadata || false
            : product?.c_fluidConfigureMetadata ||
              (!!oosBopisSkus?.length && oosBopisSkus?.indexOf(productItem?.c_sku) !== -1))
    const showGlobaleInventoryMessage =
        globaleUnavailableSKUs.indexOf(productItem?.productId) !== -1 &&
        countryISO &&
        countryISO !== 'US'
    const geRestricted =
        countryISO &&
        countryISO !== 'US' &&
        (product?.c_geIsForbidden || product?.c_geRestrictedCountries?.indexOf(countryISO) > -1)
    const {
        app: {commerceAPI}
    } = getConfig()
    const {changeUp} = commerceAPI
    const {configChangeup} = changeUp || {}
    var changeUpProductID = null
    try {
        const jsonConfig = JSON.parse(configChangeup)
        changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
    } catch (e) {
        console.log(e.message)
    }
    const isChangeUpProduct = product?.id === changeUpProductID
    const optionItems = product?.optionItems?.filter(({optionValueId}) => optionValueId !== '0')
    const {store, onOpen: setIsOpen} = useStoreContext()
    const isBopisShipment = basket?.shipments?.[0]?.shippingMethod?.c_storePickupEnabled
    return (
        <Box
            position="relative"
            className="sf-cart-item"
            data-uuid={product.itemId}
            data-testid={`sf-cart-item-${product.productId ? product.productId : product.id}`}
            sx={{
                '.storename-capitalize': {
                    color: 'black.100',
                    display: 'inline-block',
                    fontWeight: '500',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: '4px',
                    textTransform: 'capitalize',
                    fontSize: '13px',
                    lineHeight: '25px'
                }
            }}
        >
            <ItemVariantProvider variant={product}>
                {showLoading && <LoadingSpinner position="fixed" top="auto" />}
                <Stack
                    layerStyle="cardBordered"
                    align="flex-start"
                    {...containerStyles}
                    className="cart-item-stack-wrapper"
                >
                    <Flex
                        alignItems="flex-start"
                        backgroundColor="white"
                        className="cart-item-flex"
                        {...cartStyles.lineItems.item.itemFlex}
                    >
                        <CartItemVariantImage
                            imageProps={{...cartStyles.lineItems.item.imageBox.image}}
                            product={product}
                        />
                        <Stack className="item-stack" {...cartStyles.lineItems.item.itemStack}>
                            <Stack
                                spacing={1}
                                className="name-price-stack"
                                {...cartStyles.lineItems.item.namePriceStack.base}
                            >
                                <Box
                                    display="grid"
                                    gap="20px"
                                    className="name-variation-badge"
                                    maxWidth="400px"
                                >
                                    <Box
                                        className="name-variation-box"
                                        {...cartStyles.lineItems.item.nameVariation}
                                    >
                                        <Box
                                            className="name-box"
                                            {...cartStyles.lineItems.item.nameWrapper.base}
                                        >
                                            <CartItemVariantName product={product} />
                                            {optionItems?.length > 0 && (
                                                <CartItemOptionItems optionItems={optionItems} />
                                            )}
                                        </Box>
                                        <Box
                                            className="mobile-price-wrapper"
                                            display={{base: 'block', xl: 'none'}}
                                        >
                                            {!product?.c_selectedCurrency ||
                                            showPriceSkeleton ||
                                            (currencyCode &&
                                                product?.c_selectedCurrency &&
                                                product?.c_selectedCurrency !== currencyCode) ? (
                                                <Skeleton height="16px" width="49px" />
                                            ) : (
                                                <CartItemVariantPrice
                                                    currency={product?.c_selectedCurrency}
                                                    currencySymbol={currencySymbol}
                                                    productItem={productItem}
                                                />
                                            )}
                                        </Box>
                                        <CartItemVariantAttributes excludeBonusLabel />
                                    </Box>
                                    <CartItemBadges
                                        product={product}
                                        showInventoryMessage={showInventoryMessage}
                                        showBopisInventoryMessage={showBopisInventoryMessage}
                                        showGlobaleInventoryMessage={showGlobaleInventoryMessage}
                                    />
                                    {product?.c_hasEngraving &&
                                        !showBopisInventoryMessage &&
                                        !showInventoryMessage &&
                                        !showGlobaleInventoryMessage &&
                                        !geRestricted &&
                                        isBopisShipment && (
                                            <Box
                                                className="product-inventory-message"
                                                width="fit-content"
                                                {...cartStyles.lineItems.item.bopisNotAvailable}
                                            >
                                                <FormattedMessage
                                                    id="engraving.not.for.bopis"
                                                    defaultMessage="This item is not available for pickup in store. To continue, Save for Later, Remove, or choose Delivery."
                                                />
                                            </Box>
                                        )}
                                    {product &&
                                        !showBopisInventoryMessage &&
                                        (showInventoryMessage ||
                                            showGlobaleInventoryMessage ||
                                            geRestricted) && (
                                            <Box
                                                className="product-inventory-message"
                                                width="fit-content"
                                            >
                                                <Text
                                                    {...cartStyles.lineItems.item.inventoryMessage}
                                                    className="remove-product-to-continue"
                                                    fontWeight="300"
                                                >
                                                    {showGlobaleInventoryMessage ? (
                                                        <FormattedMessage
                                                            id="cart.inventory.message.globale.inventory"
                                                            defaultMessage="This item is not available for international shipping. To continue, please edit or remove."
                                                        />
                                                    ) : geRestricted ? (
                                                        <FormattedMessage
                                                            id="cart.inventory.message.globale"
                                                            defaultMessage="This Product Is Not Eligible For Shipment Outside Of The USA."
                                                        />
                                                    ) : (
                                                        <FormattedMessage
                                                            id="cart.inventory.message"
                                                            defaultMessage="This item is no longer available. Please edit or remove it to continue."
                                                        />
                                                    )}
                                                </Text>
                                            </Box>
                                        )}
                                    {showBopisInventoryMessage && (
                                        <Box
                                            className="product-inventory-message bopis-inventory-message"
                                            width="fit-content"
                                        >
                                            <Text
                                                {...cartStyles.lineItems.item.bopisNotAvailable}
                                                marginBottom="5px"
                                                className="bopis-text"
                                                onClick={(e) => {
                                                    if (
                                                        e.target.className?.indexOf('storename') !==
                                                        -1
                                                    ) {
                                                        setIsOpen(true)
                                                    }
                                                }}
                                            >
                                                <FormattedMessage
                                                    id="cart.bopis.not.available.message"
                                                    values={{
                                                        storeName:
                                                            store && 'name' in store
                                                                ? store.name.toLowerCase()
                                                                : '',
                                                        store: (chunks) => (
                                                            <span className="storename-capitalize storename">
                                                                {chunks}
                                                            </span>
                                                        )
                                                    }}
                                                    defaultMessage="Buy online, pickup in store is currently unavailable at <store>{storeName}.</store>"
                                                />
                                            </Text>
                                            <Text
                                                className="bopis-change-store"
                                                {...cartStyles.lineItems.item.bopisNotAvailable}
                                                {...cartStyles.lineItems.item
                                                    .bopisNotAvailableBlackText}
                                            >
                                                <FormattedMessage
                                                    id="cart.remove.or.change.store"
                                                    defaultMessage="To continue, remove item, change store, or call for availability."
                                                />
                                            </Text>
                                        </Box>
                                    )}
                                    {optionItems?.length > 0 && (
                                        <Box
                                            className="colorbar-note"
                                            maxWidth="495px"
                                            sx={{
                                                span: {...cartStyles.lineItems.item.productOptions}
                                            }}
                                        >
                                            <ContentAsset
                                                contentAssetId="colorbar-note"
                                                skeleton={colorBarAssetSkeleton}
                                            />
                                        </Box>
                                    )}
                                    {product?.c_hasEngraving && (
                                        <Box
                                            className="add-engraving-note"
                                            maxWidth="495px"
                                            sx={{
                                                span: {...cartStyles.lineItems.item.productOptions}
                                            }}
                                        >
                                            <ContentAsset
                                                contentAssetId="colorbar-note"
                                                skeleton={colorBarAssetSkeleton}
                                            />
                                        </Box>
                                    )}
                                </Box>
                                <Box
                                    className="desktop-price-wrapper"
                                    display={{base: 'none', xl: 'block'}}
                                >
                                    {!product?.c_selectedCurrency ||
                                    showPriceSkeleton ||
                                    (currencyCode &&
                                        product?.c_selectedCurrency &&
                                        product?.c_selectedCurrency !== currencyCode) ? (
                                        <Skeleton height="16px" width="49px" />
                                    ) : (
                                        <CartItemVariantPrice
                                            currency={product?.c_selectedCurrency}
                                            currencySymbol={currencySymbol}
                                            productItem={productItem}
                                        />
                                    )}
                                </Box>
                            </Stack>

                            <Flex
                                width="fit-content"
                                gap={{base: '20px', lg: '40px'}}
                                display={{base: 'grid', lg: 'flex'}}
                                alignItems="center"
                                className="product-quantity-actions-flex"
                            >
                                {!isChangeUpProduct && (
                                    <Stack className="product-quantity-stack" gap="10px">
                                        {isCheckout ? (
                                            <Text {...cartStyles.lineItems?.item?.quantity}>
                                                <FormattedMessage
                                                    id="cart.label.quantity"
                                                    defaultMessage="Qty"
                                                />
                                                : {quantity}
                                            </Text>
                                        ) : (
                                            <>
                                                {!(isRemoving && product.bonusProductLineItem) && (
                                                    <ProductQuantityPicker
                                                        product={product}
                                                        onItemQuantityChange={onItemQuantityChange}
                                                        stepQuantity={stepQuantity}
                                                        quantity={quantity}
                                                        setQuantity={setQuantity}
                                                    />
                                                )}
                                                {product?.c_orderLimit && (
                                                    <Box
                                                        className="quantity-limit-wrapper"
                                                        display={{base: 'block', lg: 'none'}}
                                                        width="fit-content"
                                                    >
                                                        <QuantityLimit
                                                            product={product}
                                                            isCart={true}
                                                        />
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </Stack>
                                )}
                                {!isCheckout && (
                                    <Stack> {!isChangeUpProduct && secondaryActions}</Stack>
                                )}
                            </Flex>
                            {!isCheckout && product?.c_orderLimit && (
                                <Box
                                    className="quantity-limit-wrapper"
                                    display={{base: 'none', lg: 'block'}}
                                    width="fit-content"
                                >
                                    <QuantityLimit product={product} isCart={true} />
                                </Box>
                            )}
                        </Stack>
                    </Flex>

                    <Box
                        display={['block', 'none', 'none', 'none']}
                        w={'full'}
                        className="primary-actions-wrapper"
                    >
                        {primaryAction}
                    </Box>
                </Stack>
            </ItemVariantProvider>
        </Box>
    )
}

ProductItem.propTypes = {
    product: PropTypes.object,
    onItemQuantityChange: PropTypes.func,
    outOfStockProductsList: PropTypes.array,
    onAddItemToCart: PropTypes.func,
    showLoading: PropTypes.bool,
    showPriceSkeleton: PropTypes.bool,
    currencySymbol: PropTypes.string,
    isWishlistItem: PropTypes.bool,
    primaryAction: PropTypes.node,
    oosBopisSkus: PropTypes.array,
    secondaryActions: PropTypes.node,
    containerStyles: PropTypes.object,
    productItem: PropTypes.object,
    isRemoving: PropTypes.bool,
    pickupInStore: PropTypes.bool,
    globaleUnavailableSKUs: PropTypes.array,
    isCheckout: PropTypes.bool
}

export default ProductItem
